import React from "react";
import { UseSubgraphSyncResult } from "../types";

interface SyncIndicatorProps {
  /** Sync state from useSubgraphSync hook */
  syncState: UseSubgraphSyncResult;
  /** Whether to show progress bar (default: true) */
  showProgress?: boolean;
  /** Whether to show block information (default: true) */
  showBlockInfo?: boolean;
  /** Additional CSS class names */
  className?: string;
}

/**
 * Pre-built UI component for displaying subgraph synchronization state.
 * Shows sync status, progress bar, and block information.
 *
 * @example
 * ```tsx
 * const syncState = useSubgraphSync({ blockNumber: 1000000 });
 *
 * return (
 *   <SyncIndicator
 *     syncState={syncState}
 *     showProgress={true}
 *     showBlockInfo={true}
 *   />
 * );
 * ```
 */
export const SyncIndicator: React.FC<SyncIndicatorProps> = ({
  syncState,
  showProgress = true,
  showBlockInfo = true,
  className = "",
}) => {
  const {
    isIndexing,
    isSynced,
    isError,
    currentBlock,
    targetBlock,
    progress,
    error,
  } = syncState;

  if (isError) {
    return (
      <div className={`subgraph-sync-error ${className}`}>
        ⚠️ Sync Error: {error}
      </div>
    );
  }

  if (!isIndexing && isSynced) {
    return null;
  }

  return (
    <div className={`subgraph-sync-indicator ${className}`}>
      <div className="sync-status">
        {isIndexing ? "🔄 Syncing..." : "✅ Synced"}
      </div>

      {showProgress && isIndexing && (
        <div className="sync-progress">
          <progress value={progress} max="100" />
          <span>{Math.round(progress)}%</span>
        </div>
      )}

      {showBlockInfo && currentBlock && targetBlock && (
        <div className="block-info">
          <div>Current: #{currentBlock.number}</div>
          <div>Target: #{targetBlock.number}</div>
          <div>Blocks behind: {targetBlock.number - currentBlock.number}</div>
        </div>
      )}

      {isIndexing && (
        <button
          className="refresh-button"
          onClick={() => syncState.refetch()}
          type="button"
        >
          Refresh
        </button>
      )}
    </div>
  );
};
